import React from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Props {
  page: number;
  total: number;
  limit: number;
  onChange: (p: number) => void;
}

export const Pagination: React.FC<Props> = ({ page, total, limit, onChange }) => {
  const pages = Math.max(1, Math.ceil(total / limit));

  // Build visible page numbers (window of 5)
  const getRange = () => {
    const delta = 2;
    const start = Math.max(1, page - delta);
    const end = Math.min(pages, page + delta);
    const range: number[] = [];
    for (let i = start; i <= end; i++) range.push(i);
    return range;
  };

  return (
    <div className="flex items-center justify-between py-3 text-xs text-text-muted">
      <span className="tabular-nums">
        Page {page} of {pages} &nbsp;·&nbsp; {total.toLocaleString()} total
      </span>
      <div className="flex items-center gap-1">
        <button
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:border-border-strong disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft size={14} />
        </button>

        {getRange().map((p) => (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={clsx(
              'flex h-8 min-w-[32px] items-center justify-center rounded-lg border px-2 text-xs font-semibold tabular-nums transition-all',
              p === page
                ? 'bg-gold-gradient border-primary/50 text-on-primary shadow-soft'
                : 'border-border bg-surface text-text-secondary hover:bg-surface-alt hover:border-border-strong',
            )}
          >
            {p}
          </button>
        ))}

        <button
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:border-border-strong disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};
